<template>
  <div class="video-upload-page">
    <!-- 步骤指引 -->
    <div class="steps-container">
      <VideoPipelineSteps :current="currentStep" class="upload-steps" />
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <a-card :title="$t('video.videoUpload')" :bordered="false" class="upload-card">
        <template #extra v-if="projectId">
          <a-button type="default" @click="goToProjectList">
            <arrow-left-outlined />
            {{ $t('video.backToProjectList') }}
          </a-button>
        </template>
        <!-- 项目文件夹 + 条目准备 -->
        <div class="project-info" v-if="!projectId">
          <div class="project-header">
            <h3>{{ $t('project.titleCombo.prepareWorkItem') }}</h3>
            <a-button 
              type="link" 
              size="small" 
              @click="clearInvalidProjectCache"
              v-if="hasStoredProject"
            >
              {{ $t('video.clearCache') }}
            </a-button>
          </div>
          <a-form
            :model="projectForm"
            :rules="projectRules"
            layout="vertical"
            @finish="createProject"
            ref="projectFormRef"
          >
            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item :label="$t('project.name')" name="title">
                  <ProjectTitleCombo
                    v-model="projectForm.title"
                    v-model:project-id="lockedFolderProjectId"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item :label="$t('project.titleCombo.sourceLanguage')" name="original_language">
                  <a-select
                    v-model:value="projectForm.original_language"
                    :placeholder="$t('project.selectOriginalLanguage')"
                    :options="sourceLanguageOptions"
                    :loading="languageLoading"
                    show-search
                    :filter-option="filterLanguageOption"
                  />
                </a-form-item>
              </a-col>
            </a-row>
            <a-row :gutter="16">
              <a-col :span="12">
                <a-form-item :label="$t('project.titleCombo.targetLanguage')" name="target_language">
                  <a-select
                    v-model:value="projectForm.target_language"
                    :placeholder="$t('project.titleCombo.selectTargetLanguage')"
                    :options="targetLanguageOptions"
                    :loading="languageLoading"
                    show-search
                    :filter-option="filterLanguageOption"
                  />
                </a-form-item>
              </a-col>
            </a-row>
            <a-form-item>
              <a-space>
                <a-button type="primary" html-type="submit" :loading="creating">
                  {{ $t('project.titleCombo.prepareWorkItem') }}
                </a-button>
                <a-button @click="$router.back()">{{ $t('common.cancel') }}</a-button>
              </a-space>
            </a-form-item>
          </a-form>
        </div>

        <!-- 视频上传区域 -->
        <div class="upload-section" v-if="projectId">
          <div class="section-header">
            <h3>
              <video-camera-outlined />
              {{ $t('video.videoFileUpload') }}
            </h3>
            <p class="section-description">
              {{ $t('video.dragUploadDescription') }}
            </p>
          </div>

          <!-- 拖拽上传区域 -->
          <div class="upload-area">
            <a-upload-dragger
              v-model:file-list="videoFileList"
              name="video"
              :multiple="false"
              :before-upload="beforeVideoUpload"
              :custom-request="handleVideoUpload"
              :show-upload-list="false"
              :disabled="videoUploading || !!videoFile"
              class="video-uploader"
              @drop="handleDrop"
            >
              <!-- 默认上传提示 -->
              <div v-if="!videoFile && !videoUploading" class="upload-placeholder">
                <p class="upload-icon">
                  <video-camera-outlined />
                </p>
                <p class="upload-text">{{ $t('video.clickOrDragToUpload') }}</p>
                <div class="upload-hint">
                  <p><strong>{{ $t('video.supportedFormats') }}：</strong>{{ $t('video.videoFormats') }}</p>
                  <p><strong>{{ $t('video.audioFormats') }}：</strong>{{ $t('video.audioFormatsList') }}</p>
                  <p><strong>{{ $t('video.fileSize') }}：</strong>{{ $t('video.maxFileSize') }}</p>
                  <p><strong>{{ $t('video.autoProcessing') }}：</strong>{{ $t('video.autoExtractAudio') }}</p>
                </div>
              </div>

              <!-- 上传进度 -->
              <div v-if="videoUploading" class="upload-progress">
                <a-progress
                  type="circle"
                  :percent="videoUploadProgress"
                  :width="100"
                  :stroke-color="progressColor"
                />
                <div class="progress-info">
                  <p class="progress-text">{{ uploadStatusText }}</p>
                  <p class="progress-detail">{{ uploadDetailText }}</p>
                  <div class="progress-steps" v-if="processingSteps.length > 0">
                    <a-steps
                      :current="currentProcessingStep"
                      size="small"
                      direction="vertical"
                      class="mini-steps"
                    >
                      <a-step
                        v-for="(step, index) in processingSteps"
                        :key="index"
                        :title="step.title"
                        :description="step.description"
                        :status="getStepStatus(index)"
                      />
                    </a-steps>
                  </div>
                  <div class="upload-actions">
                    <a-button 
                      type="default" 
                      size="small" 
                      @click="cancelVideoUpload"
                      :loading="false"
                    >
                      <stop-outlined />
                      {{ $t('video.cancelUpload') }}
                    </a-button>
                  </div>
                </div>
              </div>

              <!-- 上传成功 -->
              <div v-if="videoFile && !videoUploading" class="upload-success">
                <div class="success-content">
                  <div class="file-preview">
                    <div class="file-icon">
                      <video-camera-outlined />
                    </div>
                    <div class="file-info">
                      <h4 class="file-name">{{ videoFile.original_filename || videoFile.filename }}</h4>
                      <div class="file-details">
                        <a-descriptions size="small" :column="2">
                          <a-descriptions-item :label="$t('video.fileSize')">
                            {{ formatFileSize(videoFile.file_size) }}
                          </a-descriptions-item>
                          <a-descriptions-item :label="$t('video.uploadTime')">
                            {{ formatTime(videoFile.upload_time) }}
                          </a-descriptions-item>
                          <a-descriptions-item :label="$t('video.videoDuration')" v-if="videoFile.file_info?.duration">
                            {{ formatDuration(videoFile.file_info.duration) }}
                          </a-descriptions-item>
                          <a-descriptions-item :label="$t('video.resolution')" v-if="videoFile.file_info?.resolution">
                            {{ videoFile.file_info.resolution }}
                          </a-descriptions-item>
                          <a-descriptions-item :label="$t('video.audioExtracted')" v-if="videoFile.file_info?.has_audio">
                            <a-tag color="success">
                              <sound-outlined /> {{ $t('video.audioExtracted') }}
                            </a-tag>
                          </a-descriptions-item>
                          <a-descriptions-item :label="$t('video.storagePath')">
                            <a-tag color="blue">{{ $t('video.localStorage') }}</a-tag>
                          </a-descriptions-item>
                        </a-descriptions>
                      </div>
                    </div>
                  </div>
                  <div class="file-actions">
                    <a-space direction="vertical">
                      <a-button type="primary" ghost @click="previewVideo" v-if="canPreview">
                        <play-circle-outlined /> {{ $t('video.previewVideo') }}
                      </a-button>
                      <a-button danger @click="clearVideoFile">
                        <delete-outlined /> {{ $t('video.reupload') }}
                      </a-button>
                    </a-space>
                  </div>
                </div>
              </div>
            </a-upload-dragger>
          </div>

          <!-- 字幕文件上传区域（可选） -->
          <div class="subtitle-section" v-if="videoFile">
            <div class="section-header">
              <h3>
                <file-text-outlined />
                {{ $t('video.subtitleFileUploadOptional') }}
              </h3>
              <p class="section-description">
                {{ $t('video.subtitleUploadDescription') }}
              </p>
            </div>

            <div class="subtitle-upload-area">
              <a-upload-dragger
                v-model:file-list="subtitleFileList"
                name="subtitle"
                :multiple="false"
                :before-upload="beforeSubtitleUpload"
                :custom-request="handleSubtitleUpload"
                :show-upload-list="false"
                :disabled="subtitleUploading || !!subtitleFile"
                class="subtitle-uploader"
              >
                <!-- 默认上传提示 -->
                <div v-if="!subtitleFile && !subtitleUploading" class="upload-placeholder small">
                  <p class="upload-icon small">
                    <file-text-outlined />
                  </p>
                  <p class="upload-text">{{ $t('video.dragSubtitleFileHere') }}</p>
                  <div class="upload-hint">
                    <p><strong>{{ $t('video.supportedFormats') }}：</strong>{{ $t('video.subtitleFormats') }}</p>
                    <p><strong>{{ $t('video.fileSize') }}：</strong>{{ $t('video.maxSubtitleSize') }}</p>
                  </div>
                </div>

                <!-- 上传进度 -->
                <div v-if="subtitleUploading" class="upload-progress small">
                  <a-progress
                    type="circle"
                    :percent="subtitleUploadProgress"
                    :width="60"
                  />
                  <p class="progress-text">{{ $t('video.uploadingSubtitle') }}</p>
                </div>

                <!-- 上传成功 -->
                <div v-if="subtitleFile && !subtitleUploading" class="upload-success small">
                  <div class="file-info">
                    <file-text-outlined class="file-icon" />
                    <div class="file-details">
                      <p class="file-name">{{ subtitleFile.filename }}</p>
                      <p class="file-size">{{ formatFileSize(subtitleFile.file_size) }}</p>
                    </div>
                  </div>
                  <a-button type="link" @click="reuploadSubtitleFile">
                    <reload-outlined /> {{ $t('video.reupload') }}
                  </a-button>
                </div>
              </a-upload-dragger>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="action-buttons" v-if="videoFile">
            <a-space>
              <a-button @click="$router.back()">{{ $t('common.cancel') }}</a-button>
              <a-button
                type="primary"
                @click="handleNext"
                :loading="processing"
                size="large"
              >
                {{ $t('video.nextStepSubtitleEdit') }}
                <arrow-right-outlined />
              </a-button>
            </a-space>
          </div>
        </div>
      </a-card>
    </div>

    <!-- 用户引导弹窗 -->
    <a-modal
      v-model:open="showGuideModal"
      :title="$t('video.uploadGuide')"
      :footer="null"
      width="600px"
      class="guide-modal"
    >
      <div class="guide-content">
        <a-steps direction="vertical" size="small" :current="guideStep">
          <a-step :title="$t('video.prepareVideoFile')">
            <template #description>
              <div class="guide-step-content">
                <p>{{ $t('video.ensureVideoRequirements') }}</p>
                <ul>
                  <li>{{ $t('video.formatRequirement') }}</li>
                  <li>{{ $t('video.sizeRequirement') }}</li>
                  <li>{{ $t('video.qualityRequirement') }}</li>
                  <li>{{ $t('video.audioRequirement') }}</li>
                </ul>
              </div>
            </template>
          </a-step>
          <a-step :title="$t('video.uploadVideoStep')">
            <template #description>
              <div class="guide-step-content">
                <p>{{ $t('video.twoUploadMethods') }}</p>
                <ul>
                  <li><strong>{{ $t('video.dragUpload') }}</strong></li>
                  <li><strong>{{ $t('video.clickUpload') }}</strong></li>
                </ul>
                <p>{{ $t('video.uploadProgressInfo') }}</p>
              </div>
            </template>
          </a-step>
          <a-step :title="$t('video.selectSourceLanguage')">
            <template #description>
              <div class="guide-step-content">
                <p>{{ $t('video.selectSourceLanguageDescription') }}</p>
                <ul>
                  <li>{{ $t('video.multipleLanguageSupport') }}</li>
                  <li>{{ $t('video.accurateLanguageHelp') }}</li>
                  <li>{{ $t('video.canAdjustLater') }}</li>
                </ul>
              </div>
            </template>
          </a-step>
          <a-step :title="$t('video.optionalUploadSubtitle')">
            <template #description>
              <div class="guide-step-content">
                <p>{{ $t('video.ifHaveSubtitleFile') }}</p>
                <ul>
                  <li>{{ $t('video.subtitleFormatSupport') }}</li>
                  <li>{{ $t('video.autoParseTimeline') }}</li>
                  <li>{{ $t('video.autoGenerateIfNotUpload') }}</li>
                </ul>
              </div>
            </template>
          </a-step>
        </a-steps>
        <div class="guide-actions">
          <a-space>
            <a-button @click="showGuideModal = false">{{ $t('video.closeGuide') }}</a-button>
            <a-button type="primary" @click="nextGuideStep" v-if="guideStep < 3">
              {{ $t('video.nextStep') }}
            </a-button>
            <a-button type="primary" @click="showGuideModal = false" v-else>
              {{ $t('video.startUpload') }}
            </a-button>
          </a-space>
        </div>
      </div>
    </a-modal>

    <!-- 视频预览弹窗 -->
    <a-modal
      v-model:open="showPreviewModal"
      :title="$t('video.videoPreview')"
      :footer="null"
      width="800px"
      class="preview-modal"
    >
      <div class="video-preview" v-if="videoFile">
        <video
          ref="previewVideoRef"
          :src="videoPreviewUrl"
          controls
          preload="metadata"
          style="width: 100%; max-height: 400px;"
          @error="handleVideoError"
          @loadstart="handleVideoLoadStart"
          @loadeddata="handleVideoLoaded"
        >
          {{ $t('video.browserNotSupportVideo') }}
        </video>
        <div v-if="videoError" class="video-error">
          <a-alert
            :message="videoError"
            type="error"
            show-icon
            style="margin-top: 16px;"
          />
          <a-button type="primary" @click="retryVideoLoad" style="margin-top: 12px;">
            {{ $t('video.retryLoad') }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { message, Modal } from 'ant-design-vue'
import {
  VideoCameraOutlined,
  FileTextOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  SoundOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  StopOutlined
} from '@ant-design/icons-vue'
import type { UploadProps, FormInstance } from 'ant-design-vue'
import { videoApi } from '@/modules/video/api'
import { ProjectType, getProject } from '@/modules/project/api'
import {
  createWorkItem,
  getWorkItem,
} from '@/modules/project/api/work-item'
import ProjectTitleCombo from '@/modules/project/components/ProjectTitleCombo.vue'
import VideoPipelineSteps from '@/modules/video/components/VideoPipelineSteps.vue'
import { resolveProjectForUpload } from '@/modules/project/composables/resolveProjectForUpload'
import { loadUploadEntryContext, toUploadPageEntryState } from '@/modules/project/utils/upload-entry-query'
import { languageApi, LanguageType } from '@/modules/system/api/language'
import { useI18n } from 'vue-i18n'
import { formatFileSize } from '@/shared/utils'
import { runApiBatch } from '@/shared/api/use-api-batch'
import { videoPlaybackUrl } from '@/modules/video/utils/video-playback-url'

const { t } = useI18n()

const router = useRouter()
const route = useRoute()

/** 返回「我的项目」列表，便于继续创建或管理项目 */
const goToProjectList = () => {
  router.push({ name: 'MyProjects' })
}

// 响应式数据
const currentStep = ref(0)
/** 页面内业务 ID：实际为 WorkItem.id（热修；10.4 再统一命名） */
const projectId = ref<number>()
const creating = ref(false)
const languageLoading = ref(false)
const videoUploading = ref(false)
const subtitleUploading = ref(false)
const processing = ref(false)
const videoUploadProgress = ref(0)
const subtitleUploadProgress = ref(0)
const currentProcessingStep = ref(0)
const hasStoredProject = ref(false)
const uploadCancelToken = ref<AbortController | null>(null)

// 表单相关
const projectFormRef = ref<FormInstance>()
/** 下拉点选锁定的文件夹 ID；未点选则为 null，提交时走 resolve */
const lockedFolderProjectId = ref<number | null>(null)
const projectForm = reactive({
  title: '',
  original_language: '',
  target_language: undefined as string | undefined,
})

const projectRules = computed(() => ({
  title: [
    { required: true, message: t('project.titleCombo.titleRequired'), trigger: 'blur' },
    { min: 2, max: 100, message: t('video.titleLengthRequirement'), trigger: 'blur' }
  ],
  original_language: [
    { required: true, message: t('project.selectOriginalLanguage'), trigger: 'change' }
  ],
  target_language: [
    { required: true, message: t('project.titleCombo.selectTargetLanguage'), trigger: 'change' }
  ],
}))

// 文件相关
const videoFileList = ref([])
const subtitleFileList = ref([])
const videoFile = ref<any>()
const subtitleFile = ref<any>()

// 语言选项
const sourceLanguageOptions = ref<Array<{ label: string; value: string }>>([])
const targetLanguageOptions = ref<Array<{ label: string; value: string }>>([])

// 处理步骤
const processingSteps = ref([
  { title: t('video.uploadingFile'), description: t('video.uploadingVideoFile') },
  { title: t('video.validatingFormat'), description: t('video.validatingFileFormat') },
  { title: t('video.extractingInfo'), description: t('video.extractingVideoInfo') },
  { title: t('video.extractingAudio'), description: t('video.extractingAudioTrack') },
  { title: t('video.localStorage'), description: t('video.savingToLocalStorage') },
  { title: t('video.processingComplete'), description: t('video.fileProcessingComplete') }
])

// 引导相关
const showGuideModal = ref(false)
const guideStep = ref(0)
const showPreviewModal = ref(false)
const previewVideoRef = ref<HTMLVideoElement>()
const videoError = ref('')
const videoPreviewUrl = ref('')

// 计算属性
const uploadStatusText = computed(() => {
  if (videoUploadProgress.value < 50) {
    return t('video.uploadingVideoFile')
  } else if (videoUploadProgress.value < 80) {
    return t('video.processingVideoFile')
  } else {
    return t('video.almostComplete')
  }
})

const uploadDetailText = computed(() => {
  if (currentProcessingStep.value < processingSteps.value.length) {
    return processingSteps.value[currentProcessingStep.value]?.description || ''
  }
  return t('video.fileProcessingComplete')
})


const progressColor = computed(() => {
  if (videoUploadProgress.value < 30) return '#1890ff'
  if (videoUploadProgress.value < 70) return '#52c41a'
  return '#722ed1'
})

const canPreview = computed(() => {
  return videoFile.value && videoFile.value.file_info?.file_type === 'video'
})

// 方法
const loadLanguageOptions = async () => {
  try {
    languageLoading.value = true
    const [sourceResult, targetResult] = await runApiBatch([
      (ctx) =>
        languageApi.getLanguageOptions(
          {
            language_type: LanguageType.SOURCE,
            status: true,
          },
          ctx.requestConfig,
        ),
      (ctx) =>
        languageApi.getLanguageOptions(
          {
            language_type: LanguageType.TARGET,
            status: true,
          },
          ctx.requestConfig,
        ),
    ])
    if (sourceResult.status === 'fulfilled') {
      sourceLanguageOptions.value = sourceResult.value.map((option) => ({
        label: option.label,
        value: option.value,
        key: option.value,
      }))
    }
    if (targetResult.status === 'fulfilled') {
      targetLanguageOptions.value = targetResult.value.map((option) => ({
        label: option.label,
        value: option.value,
        key: option.value,
      }))
    }
  } catch (error) {
    console.error('Failed to get language options:', error)
  } finally {
    languageLoading.value = false
  }
}

const filterLanguageOption = (input: string, option: any) => {
  return option.label.toLowerCase().includes(input.toLowerCase())
}

const createProject = async () => {
  try {
    creating.value = true
    localStorage.removeItem('current_video_project')

    const folderId = await resolveProjectForUpload({
      title: projectForm.title,
      lockedProjectId: lockedFolderProjectId.value,
    })
    const workItem = await createWorkItem({
      project_id: folderId,
      item_type: ProjectType.VIDEO,
      target_language: projectForm.target_language,
      source_language: projectForm.original_language || undefined,
    })

    projectId.value = workItem.id
    currentStep.value = 1
    message.success(t('project.titleCombo.workItemReady'))
    localStorage.setItem('current_video_project', JSON.stringify(workItem))
  } catch (error: unknown) {
    console.error('Failed to prepare work item:', error)
  } finally {
    creating.value = false
  }
}

const beforeVideoUpload: UploadProps['beforeUpload'] = (file) => {
  // 验证文件类型
  const allowedVideoTypes = [
    'video/mp4', 'video/avi', 'video/quicktime', 'video/x-msvideo',
    'video/x-ms-wmv', 'video/x-flv', 'video/webm', 'video/x-matroska',
    'video/mp4v-es', 'video/3gpp', 'video/ogg'
  ]
  
  const allowedAudioTypes = [
    'audio/wav', 'audio/mpeg', 'audio/aac', 'audio/flac', 'audio/ogg'
  ]
  
  const allAllowedTypes = [...allowedVideoTypes, ...allowedAudioTypes]
  
  const isAllowedType = file.type && allAllowedTypes.includes(file.type)
  if (!isAllowedType) {
    message.error(t('video.unsupportedFileFormat'))
    return false
  }

  // 验证文件大小 (2GB)
  const isLt2G = file.size / 1024 / 1024 / 1024 < 2
  if (!isLt2G) {
    message.error(t('video.fileSizeExceedsLimit'))
    return false
  }

  return true // 允许上传，使用自定义上传处理
}

const beforeSubtitleUpload: UploadProps['beforeUpload'] = (file) => {
  // 验证文件扩展名
  const allowedExtensions = ['.srt', '.vtt', '.ass', '.ssa']
  const fileName = file.name.toLowerCase()
  const isAllowedType = allowedExtensions.some(ext => fileName.endsWith(ext))
  
  if (!isAllowedType) {
    message.error(t('video.unsupportedSubtitleFormat'))
    return false
  }

  // 验证文件大小 (10MB)
  const isLt10M = file.size / 1024 / 1024 < 10
  if (!isLt10M) {
    message.error(t('video.subtitleSizeExceedsLimit'))
    return false
  }

  return true // 允许上传，使用自定义上传处理
}

const handleVideoUpload = async (options: any) => {
  const { file } = options
  
  if (!projectId.value) {
    message.error(t('video.pleaseCreateProjectFirst'))
    return
  }
  
  try {
    videoUploading.value = true
    videoUploadProgress.value = 0
    currentProcessingStep.value = 0
    
    uploadCancelToken.value = new AbortController()
    
    const payload = await videoApi.uploadVideo(
      projectId.value, 
      file,
      (progress: number) => {
        videoUploadProgress.value = progress
        
        if (progress < 20) {
          currentProcessingStep.value = 0
        } else if (progress < 40) {
          currentProcessingStep.value = 1
        } else if (progress < 60) {
          currentProcessingStep.value = 2
        } else if (progress < 80) {
          currentProcessingStep.value = 3
        } else if (progress < 95) {
          currentProcessingStep.value = 4
        } else {
          currentProcessingStep.value = 5
        }
      },
      uploadCancelToken.value.signal
    )

    if (payload) {
      const raw = payload as unknown as Record<string, unknown>
      let actualFilename = String(raw.filename ?? '')
      const filePath = raw.file_path ?? raw.video_file_path
      if (typeof filePath === 'string' && filePath.includes('/')) {
        const pathParts = filePath.split('/')
        actualFilename = pathParts[pathParts.length - 1] || actualFilename
      }
      const fileInfo = (raw.file_info ?? raw.video_info ?? {}) as Record<string, unknown>
      
      videoFile.value = {
        filename: actualFilename,
        original_filename: (file && (file as File).name) || actualFilename,
        file_size: Number(raw.file_size ?? 0),
        upload_time: String(raw.upload_time ?? new Date().toISOString()),
        file_info: fileInfo,
        file_storage_id: Number(fileInfo.file_storage_id) || undefined,
      }
    }

    videoUploadProgress.value = 100
    currentProcessingStep.value = processingSteps.value.length - 1

    // 上传接口已内含音频提取；刷新条目缓存即可
    const workItem = await getWorkItem(projectId.value)
    localStorage.setItem('current_video_project', JSON.stringify(workItem))

    message.success(t('video.videoUploadSuccess'))
    
  } catch (error: any) {
    console.error('Video upload failed:', error)
    
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      return
    }
    
    if (error.response?.status === 502) {
      console.error('502 Bad Gateway - Possible nginx proxy timeout or backend service issue')
    }
  } finally {
    videoUploading.value = false
    uploadCancelToken.value = null
  }
}

const handleSubtitleUpload = async (options: any) => {
  const { file } = options
  
  if (!projectId.value) {
    message.error(t('video.projectIdNotExists'))
    return
  }
  
  try {
    subtitleUploading.value = true
    subtitleUploadProgress.value = 0
    
    const response = await videoApi.uploadSubtitle(
      projectId.value,
      file,
      projectForm.original_language,
      (progress) => {
        subtitleUploadProgress.value = progress
      }
    )
    
    subtitleFile.value = response
    message.success(t('video.subtitleUploadSuccess'))
    
  } catch (error: any) {
    console.error('Subtitle upload failed:', error)
  } finally {
    subtitleUploading.value = false
  }
}

const handleDrop = (e: DragEvent) => {
  e.preventDefault()
}

const clearVideoFile = async () => {
  Modal.confirm({
    title: t('video.confirmDelete'),
    content: t('video.deleteVideoFileConfirm'),
    okText: t('common.ok'),
    cancelText: t('common.cancel'),
    onOk: async () => {
      try {
        if (projectId.value) {
          await videoApi.deleteProjectFiles(projectId.value)
        }
        videoFile.value = undefined
        videoFileList.value = []
        message.success(t('video.fileDeleted'))
      } catch (error: any) {
        console.error('删除文件失败:', error)
      }
    }
  })
}

// 取消上传
const cancelVideoUpload = () => {
  if (uploadCancelToken.value) {
    uploadCancelToken.value.abort()
    uploadCancelToken.value = null
    videoUploading.value = false
    videoUploadProgress.value = 0
    currentProcessingStep.value = 0
    message.info(t('video.uploadCancelled'))
  }
}

const reuploadSubtitleFile = async () => {
  try {
    if (projectId.value) {
      // 调用后端API删除字幕文件
      await videoApi.deleteSubtitle(projectId.value)
    }
    subtitleFile.value = undefined
    subtitleFileList.value = []
    message.success(t('video.subtitleFileCleared'))
  } catch (error: any) {
    console.error('清除字幕文件失败:', error)
  }
}

const previewVideo = async () => {
  showPreviewModal.value = true
  videoError.value = ''
  videoPreviewUrl.value = ''
  
  // 异步获取视频URL
  const url = await getVideoPreviewUrl()
  videoPreviewUrl.value = url
  
  nextTick(() => {
    if (previewVideoRef.value && videoPreviewUrl.value) {
      previewVideoRef.value.load()
    }
  })
}

const handleVideoError = (e: Event) => {
  const video = e.target as HTMLVideoElement
  const error = video.error
  if (error) {
    let errorMsg = '视频加载失败'
    switch (error.code) {
      case error.MEDIA_ERR_ABORTED:
        errorMsg = '视频加载被中止'
        break
      case error.MEDIA_ERR_NETWORK:
        errorMsg = '网络错误，请检查网络连接'
        break
      case error.MEDIA_ERR_DECODE:
        errorMsg = '视频解码失败，文件可能已损坏'
        break
      case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
        errorMsg = '视频格式不支持或文件路径错误'
        break
    }
    videoError.value = errorMsg
    console.error('视频加载错误:', error, 'URL:', videoPreviewUrl.value)
  }
}

const handleVideoLoadStart = () => {
  videoError.value = ''
}

const handleVideoLoaded = () => {
  videoError.value = ''
}

const retryVideoLoad = () => {
  videoError.value = ''
  if (previewVideoRef.value) {
    previewVideoRef.value.load()
  }
}

const getVideoPreviewUrl = async () => {
  if (!projectId.value) return ''
  const localStorageId = Number(videoFile.value?.file_storage_id)
  if (localStorageId) {
    return videoPlaybackUrl({ file_storage_id: localStorageId })
  }
  try {
    const workItem = await getWorkItem(projectId.value)
    const url = videoPlaybackUrl(workItem)
    if (url) return url
  } catch (e) {
    console.error('获取条目信息失败:', e)
  }
  return ''
}

const handleNext = async () => {
  if (!videoFile.value) {
    message.error(t('video.pleaseUploadVideoFirst'))
    return
  }
  
  try {
    processing.value = true
    // 文件夹无 status；状态由 WorkItem.current_task 派生，此处直接进入字幕编辑
    router.push(`/video/subtitle-edit/${projectId.value}`)
  } catch (error: unknown) {
    console.error('进入字幕编辑失败:', error)
  } finally {
    processing.value = false
  }
}

const getStepStatus = (index: number) => {
  if (index < currentProcessingStep.value) return 'finish'
  if (index === currentProcessingStep.value) return 'process'
  return 'wait'
}

const nextGuideStep = () => {
  if (guideStep.value < 3) {
    guideStep.value++
  }
}

const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

const formatTime = (timeString: string) => {
  return new Date(timeString).toLocaleString()
}

// 清理无效的项目缓存
const clearInvalidProjectCache = () => {
  localStorage.removeItem('current_video_project')
  projectId.value = undefined
  currentStep.value = 0
  videoFile.value = undefined
  subtitleFile.value = undefined
  videoFileList.value = []
  subtitleFileList.value = []
  hasStoredProject.value = false
  
  projectForm.title = ''
  projectForm.original_language = ''
  projectForm.target_language = undefined
  lockedFolderProjectId.value = null
  
  message.info(t('video.invalidProjectCacheCleared'))
}

// 生命周期
onMounted(async () => {
  const state = toUploadPageEntryState(
    await loadUploadEntryContext(route.query, {
      getProject,
      getWorkItem,
    }),
  )
  if (state.errorLog) {
    console.error(state.errorLog.message, state.errorLog.error)
  }
  if (state.clearVideoProjectCache) {
    localStorage.removeItem('current_video_project')
  }
  projectId.value = state.projectId
  currentStep.value = state.currentStep
  lockedFolderProjectId.value = state.lockedFolderProjectId
  if (state.title) projectForm.title = state.title
  if (state.original_language) projectForm.original_language = state.original_language
  if (state.target_language) projectForm.target_language = state.target_language
  
  const storedProject = localStorage.getItem('current_video_project')
  hasStoredProject.value = !!storedProject
  
  await loadLanguageOptions()
  
  const hasSeenGuide = localStorage.getItem('video_upload_guide_seen')
  if (!hasSeenGuide && !projectId.value && lockedFolderProjectId.value == null) {
    showGuideModal.value = true
    localStorage.setItem('video_upload_guide_seen', 'true')
  }
})
</script>

<style lang="less" scoped>
.video-upload-page {
  padding: 24px;
  background: #f5f5f5;
  min-height: calc(100vh - 64px);

  .steps-container {
    margin-bottom: 24px;
    
    .upload-steps {
      background: white;
      padding: 24px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  .main-content {
    .upload-card {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border-radius: 12px;
      
      :deep(.ant-card-body) {
        padding: 32px;
      }
    }
  }

  .project-info {
    margin-bottom: 32px;
    padding-bottom: 32px;
    
    .project-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      
      h3 {
        margin: 0;
        color: #1890ff;
        font-size: 18px;
        font-weight: 600;
      }
    }
    
    border-bottom: 1px solid #f0f0f0;
  }

  .upload-section {
    .section-header {
      margin-bottom: 24px;
      
      h3 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
        font-size: 18px;
        font-weight: 600;
        color: #262626;
        
        .anticon {
          color: #1890ff;
        }
      }
      
      .section-description {
        color: #8c8c8c;
        margin: 0;
        line-height: 1.5;
      }
    }
  }

  .upload-area {
    margin-bottom: 32px;
    
    .video-uploader {
      :deep(.ant-upload-drag) {
        padding: 48px 24px;
        border: 2px dashed #d9d9d9;
        border-radius: 12px;
        background: #fafafa;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;

        &:hover {
          border-color: #1890ff;
          background: linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%);
        }

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(24, 144, 255, 0.1), transparent);
          transition: left 0.6s;
        }

        &:hover::before {
          left: 100%;
        }
      }
    }
  }

  .upload-placeholder {
    text-align: center;
    
    .upload-icon {
      font-size: 64px;
      color: #1890ff;
      margin-bottom: 16px;
      display: block;
      
      &.small {
        font-size: 48px;
      }
    }
    
    .upload-text {
      font-size: 18px;
      color: #262626;
      margin-bottom: 16px;
      font-weight: 500;
    }
    
    .upload-hint {
      color: #8c8c8c;
      line-height: 1.6;
      
      p {
        margin-bottom: 4px;
        
        strong {
          color: #595959;
        }
      }
    }
    
    &.small {
      .upload-text {
        font-size: 16px;
      }
      
      .upload-hint {
        font-size: 13px;
      }
    }
  }

  .upload-progress {
    text-align: center;
    
    .progress-info {
      margin-top: 24px;
      
      .progress-text {
        font-size: 16px;
        color: #1890ff;
        margin-bottom: 8px;
        font-weight: 500;
      }
      
      .progress-detail {
        font-size: 14px;
        color: #8c8c8c;
        margin-bottom: 16px;
      }
      
      .progress-steps {
        max-width: 300px;
        margin: 0 auto;
        
        .mini-steps {
          :deep(.ant-steps-item-title) {
            font-size: 12px;
          }
          
          :deep(.ant-steps-item-description) {
            font-size: 11px;
          }
        }
      }
      
      .upload-actions {
        margin-top: 16px;
        text-align: center;
      }
    }
    
    &.small {
      .progress-text {
        font-size: 14px;
        margin-top: 12px;
      }
    }
  }

  .upload-success {
    .success-content {
      display: flex;
      align-items: flex-start;
      gap: 24px;
      padding: 24px;
      background: linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%);
      border: 1px solid #b7eb8f;
      border-radius: 12px;
      
      .file-preview {
        flex: 1;
        display: flex;
        align-items: flex-start;
        gap: 16px;
        
        .file-icon {
          font-size: 48px;
          color: #52c41a;
          flex-shrink: 0;
        }
        
        .file-info {
          flex: 1;
          
          .file-name {
            font-size: 18px;
            font-weight: 600;
            color: #262626;
            margin-bottom: 12px;
            word-break: break-all;
          }
          
          .file-details {
            :deep(.ant-descriptions-item-label) {
              font-weight: 500;
              color: #595959;
            }
            
            :deep(.ant-descriptions-item-content) {
              color: #262626;
            }
          }
        }
      }
      
      .file-actions {
        flex-shrink: 0;
      }
    }
    
    &.small {
      .success-content {
        padding: 16px;
        
        .file-preview {
          .file-icon {
            font-size: 32px;
          }
          
          .file-info .file-name {
            font-size: 16px;
          }
        }
      }
    }
  }

  .subtitle-section {
    margin-bottom: 32px;
    
    .subtitle-upload-area {
      .subtitle-uploader {
        :deep(.ant-upload-drag) {
          padding: 32px 24px;
          border: 1px dashed #d9d9d9;
          border-radius: 8px;
          background: #fafafa;
          
          &:hover {
            border-color: #1890ff;
            background: #f0f8ff;
          }
        }
      }
    }
  }

  .action-buttons {
    text-align: right;
    padding-top: 32px;
    border-top: 1px solid #f0f0f0;
  }
}

// 引导弹窗样式
.guide-modal {
  .guide-content {
    .guide-step-content {
      margin-top: 8px;
      
      p {
        margin-bottom: 8px;
        color: #595959;
      }
      
      ul {
        margin: 8px 0;
        padding-left: 20px;
        
        li {
          margin-bottom: 4px;
          color: #8c8c8c;
          line-height: 1.5;
          
          strong {
            color: #262626;
          }
        }
      }
    }
    
    .guide-actions {
      margin-top: 24px;
      text-align: right;
      padding-top: 16px;
      border-top: 1px solid #f0f0f0;
    }
  }
}

// 预览弹窗样式
.preview-modal {
  .video-preview {
    text-align: center;
    
    video {
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .video-upload-page {
    padding: 16px;
    
    .main-content .upload-card :deep(.ant-card-body) {
      padding: 16px;
    }
    
    .upload-success .success-content {
      flex-direction: column;
      gap: 16px;
      
      .file-actions {
        align-self: stretch;
      }
    }
  }
}
</style>

